import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Typography } from '@/components/ui/typography'
import { submitSchemaValidator, useAppForm } from '@/lib/form'
import { PhotoManagerMode } from '@/lib/photo-manager-config'
import { normalizePlainText } from '@/lib/rich-text'
import { parseCurrency, getErrorMessage, readProperty } from '@/lib/utils'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  queuePosCartCustomLine,
  queuePosCartLineEdit,
  useIsCartMutating,
} from '@/lib/services/carts/carts.query'
import {
  catalogCartItemDetailsSchema,
  customCartItemSchema,
} from '@/lib/services/carts/carts.schema'
import type {
  CartItemDetailsInput,
  CreatedCart,
  CustomCartItemInput,
  PosCart,
  PosCartItem,
} from '@/lib/services/carts/carts.types'
import { toast } from 'sonner'
import { type KeyboardEvent, useEffect, useEffectEvent, useRef } from 'react'

export interface CartItemDetailsFormValues {
  name: string
  description: string
  notes: string
  recipe: string
  /** The photo MediaUploadInput saved, or the line's or past item's; the request sends its id. */
  image: NonNullable<CustomCartItemInput['image']> | null
  /** A decimal string: typed here, or the line's or a past item's price. */
  price: string
  category_id: number | null
  category_name: string | null
}

type CartItemDetailsField = keyof CartItemDetailsFormValues

interface CustomItemAdded {
  cartId: number
  customItem: CustomCartItemInput
}

type CartItemUpdated = { cartId: number; cartItem: PosCartItem } & (
  | { itemDetails: CartItemDetailsInput }
  | { customItem: CustomCartItemInput }
)

export interface CartItemDetailsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  cart: Pick<PosCart, 'id'> | null
  /** The cart to add to while `cart` hasn't loaded. */
  cartId?: number | null
  onCartIdResolved?: (cartId: number) => void
  createCartWithDefaults: () => Promise<CreatedCart>
  disabled?: boolean
  initialValues?: Partial<CartItemDetailsFormValues> | null
  focusPriceOnOpen?: boolean
  onCustomItemAdded?: (added: CustomItemAdded) => void
  /** The line being edited: a catalog line edits its details, a custom line the whole item. */
  cartItem?: PosCartItem | null
  onCustomItemUpdated?: (updated: CartItemUpdated) => void
}

interface CustomItemServerErrors {
  form: unknown[]
  fields: Partial<Record<CartItemDetailsField, unknown[]>>
}

const defaultValues: CartItemDetailsFormValues = {
  name: '',
  description: '',
  notes: '',
  recipe: '',
  image: null,
  price: '',
  category_id: null,
  category_name: null,
}

const serverFieldNameByPayloadKey: Record<string, CartItemDetailsField> = {
  'custom_item.name': 'name',
  'custom_item.description': 'description',
  'custom_item.notes': 'notes',
  'custom_item.recipe': 'recipe',
  'custom_item.price': 'price',
  'custom_item.image': 'image',
  'custom_item.image.id': 'image',
  'item_details.description': 'description',
  'item_details.notes': 'notes',
  'item_details.recipe': 'recipe',
}

function remapCustomItemServerErrors(errors: unknown): CustomItemServerErrors {
  const entries: [string, unknown][] = Object.entries(errors ?? {})

  return entries.reduce<CustomItemServerErrors>(
    (result, [key, messages]) => {
      const fieldName = serverFieldNameByPayloadKey[key]
      const normalizedMessages: unknown[] = Array.isArray(messages) ? messages : [messages]

      if (!fieldName) {
        return {
          ...result,
          form: [...result.form, ...normalizedMessages],
        }
      }

      return {
        ...result,
        fields: {
          ...result.fields,
          [fieldName]: [...(result.fields[fieldName] ?? []), ...normalizedMessages],
        },
      }
    },
    { form: [], fields: {} }
  )
}

export function CartItemDetailsModal({
  open,
  onOpenChange,
  cart,
  cartId = null,
  onCartIdResolved,
  createCartWithDefaults,
  disabled = false,
  initialValues = null,
  focusPriceOnOpen = false,
  onCustomItemAdded = undefined,
  cartItem = null,
  onCustomItemUpdated = undefined,
}: CartItemDetailsModalProps) {
  const organizationId = useRouteOrganizationId()
  const isCartMutating = useIsCartMutating()
  const didResetForCurrentOpenRef = useRef(false)
  const isEditing = Boolean(cartItem?.id)
  const isCatalogEditing =
    isEditing && cartItem?.variant_id !== null && cartItem?.variant_id !== undefined

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: submitSchemaValidator(
        isCatalogEditing ? catalogCartItemDetailsSchema : customCartItemSchema
      ),
      onSubmitAsync: async ({ value, formApi }) => {
        try {
          let currentCartId = cart?.id || cartId || (isEditing ? cartItem?.cart_id : null)

          if (!currentCartId && !isEditing) {
            const newCart = await createCartWithDefaults?.()
            currentCartId = newCart?.id

            if (currentCartId) {
              onCartIdResolved?.(currentCartId)
            }
          }

          if (!currentCartId) {
            throw new Error(
              isEditing
                ? 'Unable to update this custom item because its cart is unavailable.'
                : 'Unable to create a POS cart.'
            )
          }

          if (isCatalogEditing && cartItem) {
            const itemDetails = {
              notes: value.notes?.trim() || null,
              recipe: normalizePlainText(value.recipe, { preserveSpaces: true }) || null,
              description: value.description?.trim() || null,
            }

            await queuePosCartLineEdit(organizationId, {
              cartId: currentCartId,
              cartItemId: cartItem.id,
              itemDetails,
            })

            try {
              onCustomItemUpdated?.({ cartId: currentCartId, cartItem, itemDetails })
            } catch {
              // Optional follow-up work should not make a successful cart mutation look failed.
            }
          } else {
            const customItem: CustomCartItemInput = {
              name: value.name.trim(),
              description: value.description?.trim() || null,
              ...(value.notes?.trim() ? { notes: value.notes.trim() } : {}),
              recipe: normalizePlainText(value.recipe, { preserveSpaces: true }) || null,
              image: value.image ?? null,
              price: parseCurrency(value.price),
              category_id: value.category_id ?? null,
              category_name: value.category_name ?? null,
            }

            if (isEditing && cartItem) {
              await queuePosCartLineEdit(organizationId, {
                cartId: currentCartId,
                cartItemId: cartItem.id,
                quantity: cartItem.quantity ?? 1,
                customItem,
              })
            } else {
              await queuePosCartCustomLine(organizationId, {
                cartId: currentCartId,
                quantity: 1,
                customItem,
              })
            }

            try {
              if (isEditing && cartItem) {
                onCustomItemUpdated?.({ cartId: currentCartId, cartItem, customItem })
              } else {
                onCustomItemAdded?.({ cartId: currentCartId, customItem })
              }
            } catch {
              // Optional follow-up work should not make a successful cart mutation look failed.
            }
          }

          onOpenChange(false)
          formApi.reset()
        } catch (error) {
          toast.error(isEditing ? 'Failed to update item' : 'Failed to add custom item', {
            description: getErrorMessage(error) || 'Please try again.',
          })

          const serverErrors = readProperty(
            readProperty(readProperty(error, 'response'), 'data'),
            'errors'
          )

          if (serverErrors) {
            const remappedErrors = remapCustomItemServerErrors(serverErrors)

            return {
              form: remappedErrors.form,
              fields: remappedErrors.fields,
            }
          }
        }

        return undefined
      },
    },
  })

  // A queued write counts in isCartMutating from the moment it's queued until it answers.
  const isPending = disabled || isCartMutating > 0

  const resetForCurrentOpen = useEffectEvent(() => {
    form.reset({
      ...defaultValues,
      ...(initialValues ?? {}),
    })

    if (focusPriceOnOpen && !isCatalogEditing) {
      const focusTimeout = window.setTimeout(() => {
        const priceInput = document.getElementById('custom-item-price')

        priceInput?.focus()

        if (priceInput instanceof HTMLInputElement && priceInput.value) {
          priceInput.select()
        }
      }, 0)

      return () => window.clearTimeout(focusTimeout)
    }

    return undefined
  })

  useEffect(() => {
    if (!open) {
      didResetForCurrentOpenRef.current = false
      return undefined
    }

    if (didResetForCurrentOpenRef.current) {
      return undefined
    }

    didResetForCurrentOpenRef.current = true
    return resetForCurrentOpen()
  }, [open])

  const handleClose = () => {
    form.reset()
    onOpenChange(false)
  }

  const handleDialogOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      handleClose()
    }
  }

  const submitOnShortcut = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault()
      event.currentTarget.form?.requestSubmit()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent viewport="contained" padding="none">
        <form.AppForm>
          <form.Form className="grid max-h-[calc(100dvh-2rem)] min-h-0 grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden">
            <DialogHeader className="border-b px-6 py-4 pr-10">
              <DialogTitle>
                {isCatalogEditing
                  ? 'Edit Item Details'
                  : isEditing
                    ? 'Edit Custom Item'
                    : 'Add Custom Item'}
              </DialogTitle>
              <DialogDescription>
                {isCatalogEditing
                  ? 'Update this order item only. These changes will not affect your catalog.'
                  : isEditing
                    ? 'Update this order item only. It will not change your catalog.'
                    : 'Add a custom product for this order only. It will not be added to your catalog.'}
              </DialogDescription>
            </DialogHeader>

            <ScrollArea
              className="min-h-0 max-h-[min(32rem,60dvh)]"
              viewportClassName="h-full max-h-[min(32rem,60dvh)] overscroll-y-contain"
            >
              <div className="space-y-4 px-6 py-4">
                <form.FormError
                  title={isEditing ? 'Unable to update item' : 'Unable to add custom item'}
                />

                {!isCatalogEditing ? (
                  <>
                    <form.AppField name="name">
                      {(field) => (
                        <field.TextInput
                          id="custom-item-name"
                          label="Name"
                          placeholder="Custom product name"
                        />
                      )}
                    </form.AppField>

                    <form.AppField name="price">
                      {(field) => (
                        <field.MoneyInput
                          id="custom-item-price"
                          label="Price"
                          placeholder="0.00"
                          showFreeBadge={false}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault()
                              event.currentTarget.form?.requestSubmit()
                            }
                          }}
                        />
                      )}
                    </form.AppField>
                  </>
                ) : null}

                <form.AppField name="notes">
                  {(field) => (
                    <field.TextareaInput
                      id="item-notes"
                      label={
                        <>
                          Notes
                          <Typography variant="supporting">
                            (Optional) (Hidden from customers)
                          </Typography>
                        </>
                      }
                      placeholder="Use extra pink roses and blush ribbon..."
                      onKeyDown={submitOnShortcut}
                    />
                  )}
                </form.AppField>

                <form.AppField name="recipe">
                  {(field) => (
                    <field.TextareaInput
                      id="custom-item-recipe"
                      label={
                        <>
                          Recipe
                          <Typography variant="supporting">
                            (Optional) (Hidden from customers)
                          </Typography>
                        </>
                      }
                      placeholder={'3 Roses\n1 Lily\n...'}
                      onKeyDown={submitOnShortcut}
                    />
                  )}
                </form.AppField>

                <form.AppField name="description">
                  {(field) => (
                    <field.TextareaInput
                      id="custom-item-description"
                      label={
                        <>
                          Description
                          <Typography variant="supporting">
                            (Optional) (Visible to customers)
                          </Typography>
                        </>
                      }
                      placeholder="Includes 3 additional roses..."
                      onKeyDown={submitOnShortcut}
                    />
                  )}
                </form.AppField>

                {!isCatalogEditing ? (
                  <form.AppField name="image">
                    {(field) => (
                      <field.MediaUploadInput
                        label={
                          <>
                            Photo <Typography variant="supporting">(Optional)</Typography>
                          </>
                        }
                        placeholderClassName="h-14 w-14"
                        photoManagerMode={PhotoManagerMode.UploadOnly}
                      />
                    )}
                  </form.AppField>
                ) : null}
              </div>
            </ScrollArea>

            <DialogFooter className="shrink-0 border-t px-6 py-4">
              <Button variant="outline" type="button" onClick={handleClose} disabled={isPending}>
                Cancel
              </Button>
              <form.SubmitButton className="w-auto" mutation={{ isPending }}>
                {isEditing ? 'Save Item' : 'Add Item'}
              </form.SubmitButton>
            </DialogFooter>
          </form.Form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  )
}

export const CustomItemModal = CartItemDetailsModal
